import { useEffect, useState } from 'react';
import Icon from './Icon.jsx';
import { api } from '../lib/api.js';

// ─── "Needs you" — the client's tasks, on their home ─────────────────────────
// The client surface over the unified task store (server/tasks.js, flag
// 'tasks'): open tasks that are THEIRS (assigned to them, or organiser-owned)
// worst-first, tick-to-complete, and an expandable per-event list with the
// same readiness bar the AM board shows. Discussion lives in each task's
// Spine thread — a tap opens it in the inbox. Renders nothing when the flag
// is off (the route 404s) or there's nothing to do, per the spec.

const card = { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-sm)' };
const fmtDue = (t) => {
  if (!t.dueAt) return '';
  if (t.overdue) return `${Math.abs(t.daysToDue)}d overdue`;
  if (t.daysToDue === 0) return 'due today';
  if (t.daysToDue <= 7) return `due in ${t.daysToDue}d`;
  return `due ${new Date(t.dueAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`;
};

function Row({ t, busy, onDone, onOpenThread }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
      <button onClick={() => onDone(t)} disabled={busy} title="Mark done" aria-label={`Mark "${t.title}" done`} style={{
        width: 22, height: 22, borderRadius: 8, cursor: 'pointer', flex: 'none',
        border: '1.5px solid var(--border)', background: 'transparent',
      }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <b style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.title}</b>
          {t.withHowler && <span style={{ flex: 'none', fontSize: 10, fontWeight: 700, borderRadius: 999, padding: '1px 7px', background: 'rgba(128,128,128,0.12)', color: 'var(--muted)' }}>with Howler</span>}
        </span>
        <span style={{ fontSize: 11.5, color: t.overdue ? 'var(--error)' : 'var(--muted)', fontWeight: t.overdue ? 700 : 400 }}>
          {t.suiteName ? `${t.suiteName} · ` : ''}{fmtDue(t) || 'no due date'}
        </span>
      </div>
      {t.threadId && (
        <button onClick={() => onOpenThread(t.threadId)} title="Open the task's conversation" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--muted)', padding: 4 }}>
          <Icon name="inbox" size={15} />
        </button>
      )}
    </div>
  );
}

export default function NeedsYouTasks({ entityId, onOpenThread }) {
  const [data, setData] = useState(null);
  const [acks, setAcks] = useState([]); // unacknowledged must-ack messages (spec: they're "needs you" too)
  const [busyId, setBusyId] = useState('');
  const [showAll, setShowAll] = useState(false);
  const load = () => api.myTasks(entityId).then(setData).catch(() => setData(null));
  const loadAcks = () => api.osPending().then((r) => setAcks((r.pending || []).filter((t) => t.entityId === entityId))).catch(() => setAcks([]));
  useEffect(() => { setData(null); setAcks([]); setShowAll(false); if (entityId) { load(); loadAcks(); } }, [entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flag off (404), error, or nothing open that's theirs → take no space.
  if (!data) return null;
  const open = (data.tasks || []).filter((t) => !['done', 'na'].includes(t.status));
  const needsYou = data.needsYou || [];
  if (!needsYou.length && !open.length && !acks.length) return null;

  const ack = async (t) => {
    setBusyId(t.id);
    try { await api.osAck(t.id); await loadAcks(); window.dispatchEvent(new Event('os-refresh')); } catch { /* leave it pending */ }
    setBusyId('');
  };

  const done = async (t) => {
    setBusyId(t.id);
    try { await api.myTaskAdvance(entityId, t.id, 'done'); await load(); } catch { /* not theirs to tick */ }
    setBusyId('');
  };

  // Per-event groups for the expandable full list (readiness = done ÷ counted,
  // computed over ALL that event's tasks, same as the AM board sees).
  const byEvent = new Map();
  for (const t of data.tasks || []) {
    const key = t.suiteId || '';
    if (!byEvent.has(key)) byEvent.set(key, { label: t.suiteName || 'General', tasks: [] });
    byEvent.get(key).tasks.push(t);
  }
  const groups = [...byEvent.values()].map((g) => {
    const counted = g.tasks.filter((t) => t.status !== 'na');
    return { ...g, ready: counted.length ? Math.round((counted.filter((t) => t.status === 'done').length / counted.length) * 100) : null };
  });

  return (
    <div style={{ ...card, marginTop: 16, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="check" size={15} />
        <b style={{ fontSize: 14.5 }}>Needs you</b>
        {needsYou.length > 0 && <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '1px 8px', background: 'var(--ai-bg)', color: 'var(--ai)' }}>{needsYou.length}</span>}
        <button onClick={() => setShowAll((v) => !v)} style={{ marginLeft: 'auto', font: 'inherit', border: 'none', background: 'none', color: 'var(--brand)', cursor: 'pointer', padding: 0, fontSize: 12.5, fontWeight: 600 }}>
          {showAll ? 'Hide all tasks' : `All tasks · ${open.length}`}
        </button>
      </div>
      {acks.map((t) => (
        <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <b style={{ display: 'block', fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{t.title || '(no subject)'}</b>
            <span style={{ fontSize: 11.5, color: 'var(--ai)', fontWeight: 700 }}>Howler needs you to acknowledge this</span>
          </div>
          <button onClick={() => onOpenThread(t.id)} title="Read the message" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--muted)', padding: 4 }}>
            <Icon name="inbox" size={15} />
          </button>
          <button onClick={() => ack(t)} disabled={busyId === t.id} style={{ font: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none', background: '#b45309', color: '#fff', borderRadius: 999, padding: '6px 13px', flex: 'none' }}>
            Acknowledge
          </button>
        </div>
      ))}
      {needsYou.length === 0 && acks.length === 0 && <p style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 8 }}>Nothing waiting on you right now.</p>}
      {needsYou.map((t) => <Row key={t.id} t={t} busy={busyId === t.id} onDone={done} onOpenThread={onOpenThread} />)}
      {showAll && groups.map((g) => (
        <div key={g.label} style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <b style={{ fontSize: 12.5, color: 'var(--muted)' }}>{g.label}</b>
            {g.ready !== null && (
              <>
                <div style={{ flex: 1, maxWidth: 160, height: 5, borderRadius: 999, background: 'var(--bg)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${g.ready}%`, borderRadius: 999, background: 'linear-gradient(90deg, var(--brand), var(--brand-2))' }} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{g.ready}%</span>
              </>
            )}
          </div>
          {g.tasks.map((t) => ['done', 'na'].includes(t.status) ? (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border)', color: 'var(--muted)' }}>
              <span style={{ width: 22, height: 22, borderRadius: 8, flex: 'none', background: 'var(--success)', color: '#fff', display: 'grid', placeItems: 'center' }}><Icon name="check" size={12} /></span>
              <span style={{ fontSize: 13, textDecoration: 'line-through', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</span>
            </div>
          ) : (
            <Row key={t.id} t={t} busy={busyId === t.id} onDone={done} onOpenThread={onOpenThread} />
          ))}
        </div>
      ))}
    </div>
  );
}
